export const CARE_NAVIGATION_CRITERIA = [
  {
    id: "enrollmentCall",
    label: "Enrollment calls and initial screenings",
    defaultEnabled: true,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "ongoingCare",
    label: "Ongoing care",
    defaultEnabled: true,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "needsAssessment",
    label: "Needs assessment encounters",
    defaultEnabled: true,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "annualAssessment",
    label: "Annual assessment encounters",
    defaultEnabled: true,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "other",
    label: "Other billed encounters",
    defaultEnabled: true,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "lowTierGroups",
    label: "Low-tier group attendance",
    defaultEnabled: true,
    kpis: ["call", "carePlan"],
  },
  {
    id: "esCredit",
    label: "Credit ES calls to assigned CN",
    defaultEnabled: true,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "requireDcmp",
    label: "Require DCMP billing",
    defaultEnabled: true,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "minimumDuration",
    label: "Require calls over 15 minutes",
    defaultEnabled: false,
    kpis: ["call", "carePlan", "duration"],
  },
  {
    id: "pendingReassessments",
    label: "Include pending reassessments",
    defaultEnabled: false,
    kpis: ["reassessment"],
  },
] as const;

export type CareNavigationCriterionId = (typeof CARE_NAVIGATION_CRITERIA)[number]["id"];
export type CareNavigationCriteria = Record<CareNavigationCriterionId, boolean>;

export const DEFAULT_CARE_NAVIGATION_CRITERIA: CareNavigationCriteria = Object.fromEntries(
  CARE_NAVIGATION_CRITERIA.map(({ id, defaultEnabled }) => [id, defaultEnabled]),
) as CareNavigationCriteria;

export function resolveCareNavigationCriteria(input?: unknown): CareNavigationCriteria {
  if (input === undefined || input === null) return { ...DEFAULT_CARE_NAVIGATION_CRITERIA };
  if (typeof input !== "object" || Array.isArray(input)) throw new Error("Invalid criteria");
  const values = input as Record<string, unknown>;
  if (
    Object.keys(values).some(
      (key) => key !== "carePlanNotes" && !(key in DEFAULT_CARE_NAVIGATION_CRITERIA),
    )
  ) {
    throw new Error("Unknown care navigation criterion");
  }
  // Previous browser bundles still send this key. Validate it, then discard it.
  if ("carePlanNotes" in values && typeof values.carePlanNotes !== "boolean") {
    throw new Error("Invalid criterion: carePlanNotes");
  }
  const criteria = { ...DEFAULT_CARE_NAVIGATION_CRITERIA };
  for (const { id } of CARE_NAVIGATION_CRITERIA) {
    if (id in values) {
      if (typeof values[id] !== "boolean") throw new Error(`Invalid criterion: ${id}`);
      criteria[id] = values[id];
    }
  }
  return criteria;
}

export const CARE_NAVIGATION_TARGETS = {
  callCompletion: { minimum: 0.8, goal: 0.9 },
  carePlanCompletion: 0.95,
  averageCallMinutes: 15,
  reassessmentCompletion: 0.95,
  reassessmentTrackingStart: "2026-07-01",
} as const;

export type CareNavigationRecord = { id: string; navigatorName: string; clinicName: string };

export type CareNavigationDefinitionRequest = {
  section?: "completion" | "details" | "duration" | "reassessment";
  anchorDate?: string;
  navigatorNames?: string[];
  navigatorUserIds?: string[];
  tierNames?: string[];
  criteria?: Partial<CareNavigationCriteria>;
};

export type CareNavigationMonthlyCompletionResponse = {
  dateStart: string;
  dateEnd: string;
  cache?: "HIT" | "MISS" | "STALE";
  rows: Array<{
    monthStart: string;
    navigatorUserId: string;
    navigatorName: string;
    alignedCount: number;
    completedCount: number;
    bookedRemainingCount?: number;
    recordIds?: Record<string, string[]>;
    records?: CareNavigationRecord[];
  }>;
};

export type CareNavigationDefinitionResponse = {
  cache?: "HIT" | "MISS" | "STALE";
  dateStart: string;
  dateEnd: string;
  timezone: string;
  criteria: CareNavigationCriteria;
  snapshotDate?: string;
  emailFinalized?: boolean;
  monthlyCallDurations?: Array<{
    monthStart: string;
    navigatorUserId?: string | null;
    navigatorName: string | null;
    callDurationMinutes: number;
    callDurationCount: number;
  }>;
  durationUnavailableMonths?: string[];
  durationStaleMonths?: string[];
  rows: Array<{
    monthStart: string;
    navigatorUserId?: string;
    navigatorName: string;
    tier: string | null;
    alignedCount: number;
    assignedCount?: number;
    attritionCount?: number;
    completedCount: number;
    bookedRemainingCount: number;
    emailCompletedCount?: number | null;
    emailFinalized?: boolean;
    unbilledEmailCount?: number | null;
    recordIds?: Record<string, string[]>;
    records?: CareNavigationRecord[];
    needsAssessmentDurationMinutes?: number;
    needsAssessmentDurationCount?: number;
    callDurationMinutes: number;
    callDurationCount: number;
    durationStatus?: "available" | "loading" | "unavailable";
    callContactCount: number;
    carePlanUpdatedCount: number;
    carePlanEligibleCount: number;
    reassessmentDenominator: number;
    reassessmentCount: number;
    reassessmentInWindowCount: number;
    reassessmentUpcomingCount: number;
    reassessmentOverdueCount: number;
    reassessmentPendingCount: number;
  }>;
};
